import { Directive, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subject, merge, of, Observable } from 'rxjs';
import { FilterEnabledComponent } from 'src/app/helpers/filter-enabled.component';
import { SignPortalAssociation } from './sign-portal-association';
import { AbstractSignsPortalAssociationControllerRequestService } from './abstract-signs-portal-association-controller-request.service';
import { ModalService } from 'src/app/services/modal.service';
import { Lightbox } from 'ngx-lightbox';
import { FilterStoreService } from 'src/app/services/filter-store.service';
import {
  PageIndexService,
  PAGE_SIZE_OPTIONS,
} from 'src/app/services/page-index-service';
import {
  catchError,
  debounceTime,
  startWith,
  switchMap,
  takeUntil,
} from 'rxjs/operators';
import { MatLegacyPaginator as MatPaginator } from '@angular/material/legacy-paginator';
import { MatSort, MatSortable } from '@angular/material/sort';
import { ProperPageResult } from 'src/app/model/proper-page-result';
import { EMPTY_OBJECT } from '../../../../../globals/globals';
import { FilterObject, FILTER_OPERATION } from 'src/app/helpers/filter-object';
import { SignAssociationControllerRequestService } from 'src/app/services/api/sign-associations/sign-association-controller-request.service';
import { SignAssociationAsset } from '../../sign-associations/edit-association/sign-association-asset';

@Directive()
export abstract class AbstractSignsPortalAssociationOverviewComponent<T, O>
  extends FilterEnabledComponent
  implements OnInit, OnDestroy
{
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort: MatSort;

  private static FILTERS_CHANGED_DEBOUNCE_TIME: number = 800;

  loading: boolean = true;
  associationObjects: SignPortalAssociation[];
  totalItems: number = 0;

  protected doLoadSubject: Subject<any>;
  private destroyThisComponent = new Subject<any>();

  SIGN_ASSOCIATION_TYPE = SIGN_ASSOCIATION_TYPE;
  EMPTY_OBJECT = EMPTY_OBJECT;
  pageSizeOptions = PAGE_SIZE_OPTIONS;

  constructor(
    protected filterStoreService: FilterStoreService,
    protected pageIndexService: PageIndexService,
    protected abstractSignsPortalAssociationControllerRequestService: AbstractSignsPortalAssociationControllerRequestService,
    protected signAssociationControllerRequestService: SignAssociationControllerRequestService<
      T,
      O
    >,
    protected modalService: ModalService,
    protected _lightbox: Lightbox,
    public signAssociationType: SIGN_ASSOCIATION_TYPE
  ) {
    super(
      [
        {
          key: 'sign__participatingCompanies__company__regularName',
          active: false,
          strictEquality: false,
          numeric: false,
        },
        {
          key: 'graphicsComplete',
          active: false,
          strictEquality: true,
          numeric: false,
          alwaysTriggerRequest: true,
          operation: FILTER_OPERATION.NEGATION,
        },
        {
          key: 'adminComplete',
          active: false,
          strictEquality: true,
          numeric: false,
          alwaysTriggerRequest: true,
          operation: FILTER_OPERATION.NEGATION,
        },
      ],
      filterStoreService,
      pageIndexService
    );
  }

  ngOnInit(): void {
    this.doLoadSubject = new Subject<any>();
    const loadObservable = merge(
      this.doLoadSubject,
      this.sort.sortChange,
      this.paginator.page
    );

    this.paginator.pageIndex = this.initPageIndex;
    this.paginator.pageSize = this.initPageSize;

    this.sort.sort({
      id: 'height',
      start: 'asc',
      disableClear: true,
    } as MatSortable);

    this.initializeSubscriptions(loadObservable);
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    if (this.doLoadSubject != null) {
      this.doLoadSubject.complete();
    }
    if (this.destroyThisComponent != null) {
      this.destroyThisComponent.next();
      this.destroyThisComponent.complete();
    }
  }

  private initializeSubscriptions(loadObservable: Observable<any>): void {
    this.addSubscription(this.sort.sortChange, () => {
      this.pageIndexService.setPageIndex(this.getLocalStorageKey(), 0);
      this.paginator.pageIndex = 0;
    });

    this.addSubscription(
      this.filtersChanged.pipe(
        debounceTime(
          AbstractSignsPortalAssociationOverviewComponent.FILTERS_CHANGED_DEBOUNCE_TIME
        )
      ),
      () => {
        this.pageIndexService.setPageIndex(this.getLocalStorageKey(), 0);
        this.paginator.pageIndex = 0;
        this.doLoadSubject.next();
      }
    );

    this.addSubscription(
      loadObservable.pipe(
        startWith(EMPTY_OBJECT),
        switchMap(async () => {
          this.loading = true;
          return await this.abstractSignsPortalAssociationControllerRequestService.getPaged(
            this.generateFilterString(),
            this.sort.active,
            this.sort.direction === 'asc',
            this.paginator.pageSize,
            this.paginator.pageIndex,
            false
          );
        }),
        catchError(() => {
          return of(EMPTY_OBJECT);
        }),
        takeUntil(this.destroyThisComponent)
      ),
      (res: ProperPageResult<SignPortalAssociation>) => {
        this.associationObjects = res == null ? [] : res.items;
        this.totalItems = res == null ? 0 : res.totalItems;
        this.loading = false;
      }
    );
  }

  public abstract editItem(item: SignPortalAssociation): void;

  public deleteItem(item: SignPortalAssociation): void {
    this.modalService.confirmDialog(
      `${this.getCapitalizedAssociationName()} verwijderen`,
      `Bent u zeker dat u deze ${this.getAssociationName()} wilt verwijderen?`,
      (b: boolean) => {
        if (b) {
          this.performDelete(item);
        }
      }
    );
  }

  private async performDelete(item: SignPortalAssociation): Promise<void> {
    this.loading = true;
    try {
      await this.signAssociationControllerRequestService.archive(item.id);
      this.doLoadSubject.next();
    } catch (err) {
      this.loading = false;
      throw err;
    }
  }

  private getAssociationName(): string {
    switch (this.signAssociationType) {
      case SIGN_ASSOCIATION_TYPE.SIGN_TITLE_ADJUSTMENT:
        return 'titel aanpassing';
      case SIGN_ASSOCIATION_TYPE.SIGN_PHONE_NUMBER_ADJUSTMENT:
        return 'telefoonnummer aanpassing';
      case SIGN_ASSOCIATION_TYPE.SIGN_STICKER:
        return 'sticker';
    }
  }

  private getCapitalizedAssociationName(): string {
    const toReturn = this.getAssociationName();
    return toReturn.charAt(0).toUpperCase() + toReturn.slice(1);
  }

  openLightbox(signAssociationAssets: SignAssociationAsset[]) {
    let albums = [];
    for (let asset of signAssociationAssets) {
      if (asset.archived) {
        continue;
      }
      const src = '/api/assets/' + asset.asset.uuid;
      const caption = 'Ontwerp';
      const thumb = '/api/assets/' + asset.asset.uuid;
      const album = {
        src: src,
        caption: caption,
        thumb: thumb,
      };

      albums.push(album);
    }
    this._lightbox.open(albums, 0);
  }

  public toggleFilter(filterName: string): void {
    super.toggleFilter(filterName);
  }

  public onFilterValueChange(filterData: any): void {
    super.onFilterValueChange(filterData);
  }

  public getFilterForKey(key: string): FilterObject {
    return super.getFilterForKey(key);
  }
}

export enum SIGN_ASSOCIATION_TYPE {
  SIGN_TITLE_ADJUSTMENT = 'SIGN_TITLE_ADJUSTMENT',
  SIGN_PHONE_NUMBER_ADJUSTMENT = 'SIGN_PHONE_NUMBER_ADJUSTMENT',
  SIGN_STICKER = 'SIGN_STICKER',
}
